import { motion } from "framer-motion";
import { MapPin, Clock, Phone, MessageCircle, Zap, ShieldCheck, Timer } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/siteConfig";

const districts = [
    "الحمراء",
    "الروضة",
    "السلامة",
    "النعيم",
    "الصفا",
    "المرجان",
    "أبحر",
    "النسيم",
    "بني مالك",
    "العزيزية",
    "الحمدانية",
    "البغدادية",
];

const stats = [
    { icon: Timer, value: "١٥–٣٠", label: "دقيقة حتى الوصول" },
    { icon: ShieldCheck, value: "٠٪", label: "إتلاف عند الفتح" },
    { icon: Zap, value: "٢٤/٧", label: "جاهزية كاملة" },
];

const Location = () => (
    <section id="location" data-testid="location-section" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="mb-10 grid grid-cols-3 gap-2 sm:gap-5"
            >
                {stats.map((s, i) => (
                    <div
                        key={i}
                        data-testid={`stat-card-${i}`}
                        className="flex flex-col items-center gap-2 rounded-2xl border border-[#232F45] bg-[#151C2A] p-3 text-center sm:flex-row sm:gap-4 sm:rounded-3xl sm:p-6 sm:text-right"
                    >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/12 text-amber-400 sm:h-14 sm:w-14 sm:rounded-2xl">
                            <s.icon size={20} />
                        </span>
                        <div>
                            <p className="font-display text-lg font-black text-white sm:text-3xl">{s.value}</p>
                            <p className="text-[10px] leading-tight text-gray-400 sm:text-sm">{s.label}</p>
                        </div>
                    </div>
                ))}
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="relative mb-5 overflow-hidden rounded-[2rem] border border-[#232F45]"
                data-testid="jeddah-map-card"
            >
                <iframe
                    data-testid="jeddah-map"
                    title="خريطة تغطية خدمتنا في جدة"
                    src="https://maps.google.com/maps?q=Jeddah,+Saudi+Arabia&z=11&output=embed"
                    className="h-72 w-full sm:h-80"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                />
                <div
                    data-testid="map-coverage-badge"
                    className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full border border-white/10 bg-[#0B0F17]/85 px-4 py-2 text-xs font-bold text-amber-400 backdrop-blur-md"
                >
                    <MapPin size={14} />
                    تغطية كل أحياء جدة — نصلك أينما كنت
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6 }}
                data-testid="districts-list"
                className="mb-12 flex flex-wrap items-center justify-center gap-2 text-center"
            >
                <span className="w-full text-sm font-bold text-gray-300 sm:w-auto">
                    نخدم جميع أحياء جدة، منها:
                </span>
                {districts.map((d) => (
                    <span
                        key={d}
                        className="rounded-full border border-[#232F45] bg-[#151C2A] px-3.5 py-1.5 text-xs font-bold text-gray-300"
                    >
                        {d}
                    </span>
                ))}
                <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1.5 text-xs font-bold text-amber-400">
                    وكل الأحياء الأخرى
                </span>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="relative overflow-hidden rounded-[2.5rem] border border-amber-500/25 bg-gradient-to-bl from-[#1A2332] to-[#0B0F17] p-8 text-center sm:p-14"
            >
                <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/12 blur-[100px]" />

                <div className="relative z-10">
                    <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
                        <span
                            data-testid="location-badge"
                            className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-gray-200"
                        >
                            <MapPin size={16} className="text-amber-400" />
                            جدة — خدمة متنقلة نصلك أينما كنت
                        </span>
                        <span
                            data-testid="hours-badge"
                            className="flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-4 py-2 text-sm font-bold text-emerald-400"
                        >
                            <Clock size={16} />
                            مفتوح 24 ساعة · كل أيام الأسبوع
                        </span>
                    </div>

                    <h2 className="font-display mx-auto max-w-2xl text-3xl font-black leading-snug text-white sm:text-5xl">
                        عالق الآن؟ <span className="text-amber-400">اتصال واحد يكفي.</span>
                    </h2>
                    <p className="mx-auto mt-5 max-w-md text-base text-gray-400 sm:text-lg">
                        أرسل موقعك على الواتساب أو اتصل مباشرة — نؤكد السعر قبل التحرك.
                    </p>

                    <p
                        dir="ltr"
                        data-testid="location-phone-number"
                        className="font-display mt-8 text-4xl font-black tracking-wider text-amber-400 sm:text-6xl"
                    >
                        {PHONE_DISPLAY}
                    </p>

                    <div className="mt-9 flex flex-col justify-center gap-4 sm:flex-row">
                        <a
                            href={PHONE_TEL}
                            data-testid="location-call-button"
                            className="cta-shine flex items-center justify-center gap-3 rounded-2xl bg-blue-600 px-9 py-4 text-lg font-extrabold text-white shadow-[0_10px_36px_rgba(59,130,246,0.35)] transition-colors duration-300 hover:bg-blue-500"
                        >
                            <Phone size={22} />
                            اتصال مباشر
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="location-whatsapp-button"
                            className="cta-shine flex items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-9 py-4 text-lg font-extrabold text-white shadow-[0_10px_36px_rgba(34,197,94,0.3)] transition-colors duration-300 hover:bg-emerald-500"
                        >
                            <MessageCircle size={22} />
                            واتساب — أرسل موقعك
                        </a>
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

export default Location;
